import {
  Component,
  OnInit,
  ViewChild,
  DoCheck,
  ViewEncapsulation,
} from '@angular/core';
import { ChannelConfig } from '../../shared/models/cerebrum-core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { FormGroup, FormArray, FormControl, FormBuilder, Validators } from '@angular/forms';
import { FlowManagerService } from '../flow/services/flow-manager.service';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import {
  ShortcutKeywordsRequest,
  StageRequest,
} from '../../shared/models/flow-manager-core';
import { swalMini } from '../../shared/utils/utils';
import { RolesService } from '../../core/services/roles/roles.service';
import { FlowMetaData } from '../../shared/models/flow-meta-data';
import findIndex from 'lodash/findIndex';
import { MatTabGroup } from '@angular/material/tabs';
import { CMSConditions } from '../../shared/models/cms-data';
import {CMSService} from '../../shared/components/cms/service/cms.service';
import { SharedService } from '../../core/services/store/shared-store/shared-storage.service';

const STEP_NAME = ':step';
const FLOW_NAME = ':flowName';
const DUPLICATE_KEYWORD_ERROR_MESSAGE = 'keyword Already exist';
const keywordLimit = 100;

@Component({
  selector: 'app-keywords',
  templateUrl: './keywords.component.html',
  styleUrls: ['./keywords.component.scss'],
})
export class KeywordsComponent implements OnInit, DoCheck {
  chipField = {
    label: 'Keyword',
    placeholder: 'Add a Keyword',
    validators: {
      maxLength: keywordLimit,
      duplicate: true,
      required: true,
    },
  };
  flowNames: Observable<FlowMetaData[]>;

  flowMetaData$: Observable<FlowMetaData[]>;
  flows$: Observable<Map<string, string>>;
  flows: any;

  flowStepsMap = new Map();
  availableChannels: ChannelConfig[] = [];
  selectedChannelIndex = 0;
  channelId: string;
  keywordsFormGroup: FormGroup = null;
  keywordsConflictMessageFormGroup: FormGroup = null;
  globalEnvironment: any;
  savingInProgress: Boolean = false;
  bannerCmsData;

  @ViewChild(MatTabGroup, { static: true }) tabGroup: MatTabGroup;
  resetOnDelete: boolean = false;
  stepErrorMessage: String = 'Please select a value';
  incorrect: String = '';

  constructor(
    private flowManagerService: FlowManagerService,
    private rolesService: RolesService,
    public sharedService: SharedService,
    private formBuilder: FormBuilder,
    public cmsService: CMSService
  ) {}

  ngOnInit(): void {
    this.bannerCmsData = this.cmsService.cmsCall('5lDGCSCIRVkOXkdCpXgzbs', 'entry');
    this.flowManagerService
      .getAllChannelConfig()
      .pipe(
        tap((config) => {
          this.availableChannels = config;
          this.channelId =
            this.availableChannels[this.selectedChannelIndex]['channel-id'].toString();
        }),
        tap(() => {
          this.getFlowMetaData();
          this.checkFlowIssue();
          this.getFlows().pipe(
            tap((x) => {
              this.flows = x;
            }),
            map((value) => {
              this.loadKeywordsData();
            })
          );
        })
      )
      .subscribe();
  }

  checkFlowIssue() {
    const flowIssue = JSON.parse(localStorage.getItem('flowIssue'));
    if (flowIssue && flowIssue.type === 'keyword') {
      const channelIndex = findIndex(this.availableChannels, { 'channel-name': flowIssue.channel });
      if (channelIndex !== -1 && this.selectedChannelIndex !== channelIndex) {
        this.selectedChannelIndex = channelIndex;
        this.tabGroup.selectedIndex = channelIndex;
        this.channelId = this.availableChannels[this.selectedChannelIndex]['channel-id'].toString();
      }
    }
    localStorage.removeItem('flowIssue');
  }

  ngDoCheck(): void {
    if (
      JSON.stringify(this.globalEnvironment) !==
      JSON.stringify(this.sharedService.getGlobalEnvironment())
    ) {
      this.getFlowMetaData();
      this.getFlows()
        .pipe(
          tap((x) => {
            this.flows = x;
          }),
          map((value) => {
            this.loadKeywordsData();
          })
        )
        .subscribe();
      this.globalEnvironment = Object.assign({}, this.sharedService.getGlobalEnvironment());
    }
  }

  canEdit(): boolean {
    return this.rolesService.canEditKeywords(this.sharedService.getEnvironment());
  }

  checkMaxKeywords(form) {
    if (form.value.keywords && form.value.keywords.length >= 10) {
      return true;
    }
    return false;
  }

  channelChanged($event: MatTabChangeEvent) {
    this.selectedChannelIndex = $event.index;
    this.channelId = this.availableChannels[this.selectedChannelIndex]['channel-id'].toString();
    this.loadKeywordsData();
  }

  loadKeywordsData() {
    this.keywordsFormGroup = new FormGroup({});
    this.keywordsConflictMessageFormGroup = new FormGroup({});
    const keywordsChannelFormGroup = new FormArray([]);

    this.getFlowNames();

    this.flowManagerService.getKeywordMap(this.channelId).subscribe((keywords) => {
      for (const flow in keywords) {
        const keyWordSplit = flow.split(':');

        const newFormGroup = new FormGroup({
          flowName: new FormControl(
            {
              value: keyWordSplit[0],
              disabled: !this.canEdit(),
            },
            [Validators.required]
          ),
          step: new FormControl(
            {
              value: keyWordSplit[1],
              disabled: !this.canEdit(),
            },
            [Validators.required]
          ),
          keywords: this.formBuilder.array(keywords[flow].keywords, [Validators.maxLength(keywordLimit)]),
        });
        keywordsChannelFormGroup.push(newFormGroup);
      }
      this.keywordsFormGroup.addControl(this.channelId, keywordsChannelFormGroup);
    });

    this.flowManagerService.getChannelConfig(this.channelId).subscribe((channel: ChannelConfig) => {
      const newFormGroup = new FormGroup({
        conflictMessage: new FormControl(channel['keyword-conflict-message'], [
          Validators.required,
        ]),
      });
      this.keywordsConflictMessageFormGroup.addControl(this.channelId, newFormGroup);
    });
  }

  addNewKeyword() {
    const availableChannels = this.availableChannels[this.selectedChannelIndex];
    if (!this.keywordsFormGroup) {
      this.keywordsFormGroup = new FormGroup({ channelId: new FormArray([]) });
    }
    const keywordsChannelFormGroup = this.keywordsFormGroup.controls[
      availableChannels['channel-id']
    ] as FormArray;
    const newFormGroup = new FormGroup({
      flowName: new FormControl('', [Validators.required]),
      step: new FormControl('', [Validators.required]),
      keywords: new FormArray([], [Validators.maxLength(keywordLimit)]),
    });

    if (keywordsChannelFormGroup) {
      keywordsChannelFormGroup.push(newFormGroup);
    }

    setTimeout(() => {
      const element = document.querySelector('.mat-tab-body-active > .mat-tab-body-content ') as HTMLElement;
      element.scrollTo(pageXOffset, element.scrollHeight);
    }, 200);
  }

  buildCMSConditions(type: string, field: string, channelId: number, size?: string) {
      const cmsCondition: CMSConditions = {
        channelId: channelId,
        field: field,
        size: size,
        type: type
      };

      return cmsCondition;
  }

  saveKeywords() {
    if (
      this.canEdit() &&
      this.keywordsFormGroup.controls[this.channelId].valid &&
      this.keywordsConflictMessageFormGroup.controls[this.channelId].valid
    ) {
      const flowKeywords: Object = {};

      for (const keywordGroup of this.keywordsFormGroup.controls[this.channelId].value) {
        const keywordList = {};
        keywordList['keywords'] = keywordGroup.keywords;

        flowKeywords[keywordGroup.flowName + ':' + keywordGroup.step] = keywordList;
      }
      const keywordConflictMessage =
        this.keywordsConflictMessageFormGroup.controls[this.channelId].value['conflictMessage'] !==
        ''
          ? this.keywordsConflictMessageFormGroup.controls[this.channelId].value['conflictMessage']
          : 'Conflicting Message';

      const shortcutKeywordsRequest: ShortcutKeywordsRequest = {
        keywordConflictMessage: keywordConflictMessage,
        keywords: flowKeywords,
      };

      const stageRequest: StageRequest = {
        message: 'message',
        channelId: +this.channelId,
        shortcutKeywords: shortcutKeywordsRequest,
      };

      this.savingInProgress = true;
      this.flowManagerService.saveKeywords(stageRequest).subscribe(
        (isSuccessful) => {
          this.savingInProgress = false;
          if (isSuccessful) {
            swalMini({
              title: 'Keywords saved successfully!',
              type: 'success',
            });
          } else {
            swalMini({
              title: 'Oops! something went wrong while saving.',
              type: 'error',
            });
          }
        },
        (err) => {
          this.savingInProgress = false;
          swalMini({
            title: 'Oops! something went wrong while saving.',
            type: 'error',
          });
        }
      );
    } else {
      const errors = this.getFormValidationErrors(this.keywordsFormGroup.controls[this.channelId]);
      const errs = this.getFormValidationErrors(
        this.keywordsConflictMessageFormGroup.controls[this.channelId]
      );
      errors.map((error) => {
        swalMini({
          title: error || 'Validation failed, please check for errors!',
          type: 'error',
        });
      });
      if (errs.toString() === 'true') {
        swalMini({
          title: 'Conflicting Keywords is Empty!',
          type: 'error',
        });
      }
    }
  }

  getFormValidationErrors(form) {
    let errors = [];
    Object.keys(form.controls).forEach((key) => {
      const controlErrors: any = form.get(key).errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach((keyError) => {
          errors.push(controlErrors[keyError]);
        });
      }
    });
    return errors;
  }

  removeKeywordGroup(index: number) {
    this.resetOnDelete = true;
    const keywordGroup = this.keywordsFormGroup.controls[this.channelId] as FormArray;
    keywordGroup.removeAt(index);
    setTimeout(() => {
      this.keywordsFormGroup.updateValueAndValidity();
      this.keywordsFormGroup.controls[this.channelId].markAsDirty();
      // force component update
      this.resetOnDelete = false;
    }, 1);
  }

  trackByFn(index, item) {
    return index; // or item.id
  }

  /**
   * Get all the flow name
   *
   * @function this.utils.getId()
   * gets the cashed client id
   *
   * @returns Observable<string[]>
   */
  getFlowNames() {
    this.flowNames = this.flowManagerService.getFlowMetaData(true).pipe(shareReplay(1));
  }

  getFlows(): Observable<any> {
    if (!this.flows$) {
      this.flows$ = this.flowManagerService.getFlows_with_ids(true).pipe(shareReplay(1));
    }
    return this.flows$;
  }

  getFlowMetaData() {
    if (!this.flowMetaData$) {
      this.flowMetaData$ = this.flowManagerService.getFlowMetaData(true).pipe(shareReplay(1));
    }
  }

  /**
   * Get all steps for a particular flow
   *
   * @param flowId
   *
   * @function this.utils.getId()
   * gets the cashed client id
   *
   * @returns Observable<string[]>
   */

  getSteps(flowName: string, channelId: number, i: number) {
    const selectFlowId = flowName ? flowName : this.keywordsFormGroup.value[channelId][i].flowName;
    let flowSteps: any = [];

    if (this.flowStepsMap.get(selectFlowId)) {
      return this.flowStepsMap.get(selectFlowId);
    } else {
      if (this.flows) {
        flowSteps = this.flowManagerService.getFlowSteps(selectFlowId).pipe(
          map((steps: string[]) => {
            if (steps && Array.isArray(steps)) {
              return steps.sort(sortSteps);
            }
            return steps;
          }),
          shareReplay(1)
        );
        this.flowStepsMap.set(selectFlowId, flowSteps);
      }
      return flowSteps;
    }

    function sortSteps(stepA, stepB): number {
      const stepANumber = stepA.replace('step', '');
      const stepBNumber = stepB.replace('step', '');
      return stepANumber - stepBNumber;
    }
  }

  getErrorMessage(key) {
    if (!this.stepErrorMessage || this.stepErrorMessage === 'Please select a value') {
      return `Please select a ${key}`;
    }
    return this.stepErrorMessage;
  }

  onStepChange(event, keywordsGroup) {
    const currentKeywordsGroup = this.keywordsFormGroup.get(this.channelId + '.' + keywordsGroup);
    const keywordsGroupList =  this.keywordsFormGroup.get(this.channelId);
    if (currentKeywordsGroup && keywordsGroupList) {
      const currFlow = currentKeywordsGroup.value.flowName;
      for (let i = 0; i < keywordsGroupList.value.length; i++) {
        if (keywordsGroupList.value[i].flowName === currFlow
          && keywordsGroupList.value[i].step === event.value && i !== keywordsGroup) {
          currentKeywordsGroup.get('step').setErrors({'incorrect': true});
          this.stepErrorMessage = 'Please select another step';
          this.incorrect = 'incorrect';
          return;
        }
      }
      currentKeywordsGroup.get('step').setErrors(null);
      this.incorrect = '';
      this.stepErrorMessage = 'Please select a value';
    }
  }
}
